import { MagnifyingGlassIcon, DocumentTextIcon, BuildingOffice2Icon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "components/common/LoadingSpinner";
import { Customer } from "types/customer";
import { Quotation, QuotationStatus } from "types/quotation";
import { useLanguage } from "contexts/LanguageContext";
import { useState, useEffect, useMemo } from "react";

interface SelectPageProps {
  documentType: 'quotation' | 'contract' | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredItems: Quotation[] | Customer[];
  setSelectedItem: (item: Quotation | Customer) => void;
  handleNextStep: () => void;
  quotationsLoading: boolean;
  customersLoading: boolean;
}

// Státusz alapján szín meghatározása
const getStatusColor = (status: QuotationStatus): string => {
  switch (status) {
    case 'draft':
      return 'bg-gray-100 text-gray-700';
    case 'editing':
      return 'bg-blue-100 text-blue-700';
    case 'pending':
      return 'bg-amber-100 text-amber-700';
    case 'rejected':
      return 'bg-red-100 text-red-700';
    case 'success':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-purple-100 text-purple-700';
  }
};

const ITEMS_PER_PAGE = 6;

const SelectPage = ({
  documentType,
  searchQuery,
  setSearchQuery,
  filteredItems,
  setSelectedItem,
  handleNextStep,
  quotationsLoading,
  customersLoading,
}: SelectPageProps) => {
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  
  // Reset to first page when search query changes or document type changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, documentType]);
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredItems.length);
  
  // Get current page items
  const paginatedItems = useMemo(() => {
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, startIndex, endIndex]);
  
  // Pagination handlers
  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };
  
  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };
  
  return (
    <div className="flex flex-col h-[650px]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-1">
            {documentType === 'quotation' ? t('documents.selectQuotation') : t('documents.selectCustomer')}
          </h3>
          <p className="text-xs text-gray-500">
            {t('documents.selectDescription', { type: documentType === 'quotation' ? 'árajánlat' : 'ügyfél' })}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 flex-1 flex flex-col shadow-sm">
        {/* Keresés */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder={documentType === 'quotation' ? t('documents.searchQuotation') : t('documents.searchCustomer')}
              className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 pl-10 text-sm text-gray-700"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        {/* Lista */}
        <div className="flex-1 h-[400px] overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-50 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-teal-300/70">
          {(documentType === 'quotation' ? quotationsLoading : customersLoading) ? (
            <div className="flex justify-center items-center h-full">
              <LoadingSpinner size="md" color="teal-500" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500 mb-1">
                {searchQuery ? t('documents.noResults') : t('documents.noItemsAvailable')}
              </p>
            </div>
          ) : (
            <div className="space-y-2 pb-2">
              {paginatedItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    setSelectedItem(item);
                    handleNextStep();
                  }}
                  className={documentType === 'quotation' 
                    ? "flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50 cursor-pointer transition-all shadow-sm"
                    : "flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-teal-400 hover:bg-teal-50 cursor-pointer transition-all shadow-sm"
                  }
                >
                  <div className={documentType === 'quotation'
                    ? "p-2 rounded bg-gradient-to-br from-purple-100 to-purple-200 mr-3"
                    : "p-2 rounded bg-gradient-to-br from-teal-100 to-teal-200 mr-3"
                  }>
                    {documentType === 'quotation' ? (
                      <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                    ) : (
                      <BuildingOffice2Icon className="h-5 w-5 text-teal-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800">
                      {documentType === 'quotation'
                        ? (item as Quotation).number
                        : (item as Customer).type === 'organization'
                          ? (item as Customer).companyName || 'Cégnév hiányzik'
                          : `${(item as Customer).firstName || ''} ${(item as Customer).lastName || ''}`.trim() || 'Név hiányzik'
                      }
                    </div>
                    <div className="text-xs text-gray-500">
                      {documentType === 'quotation'
                        ? t('documents.createdAt', { date: (item as Quotation).createdAt.toLocaleDateString('hu-HU') })
                        : (item as Customer).type === 'organization'
                          ? t('documents.taxNumber', { number: (item as Customer).taxNumber })
                          : t('documents.email', { email: (item as Customer).email })
                      }
                    </div>
                  </div>
                  <div className="ml-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      documentType === 'quotation' 
                        ? getStatusColor((item as Quotation).status) 
                        : ((item as Customer).type === 'organization' 
                           ? 'bg-teal-100 text-teal-700' 
                           : 'bg-blue-100 text-blue-700')
                    }`}>
                      {documentType === 'quotation'
                        ? t(`documents.statuses.${(item as Quotation).status}`)
                        : (item as Customer).type === 'organization'
                          ? t('documents.organization')
                          : t('documents.individual')
                      }
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {filteredItems.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-2 py-3 mt-2">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
                  currentPage === 1 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('documents.pagination.previous')}
              </button>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
                  currentPage === totalPages 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('documents.pagination.next')}
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('documents.pagination.showing', {
                    from: startIndex + 1,
                    to: endIndex,
                    total: filteredItems.length
                  })}
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                      currentPage === 1 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">{t('documents.pagination.previous')}</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700">
                    {t('documents.pagination.page', { current: currentPage, total: totalPages })}
                  </span>
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                      currentPage === totalPages 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">{t('documents.pagination.next')}</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectPage;

